import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/App";

const EMPTY = { business_name: "", vat_number: "", tax_code: "", sdi_code: "", pec_email: "", address: "", city: "", postal_code: "", country: "IT" };

// Billing data for invoicing: business users provide VAT so invoices skip manual fiscal review
export function BillingDataDialog({ open, onClose }) {
  const [isBusiness, setIsBusiness] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoaded(false);
    api.get("/profile/billing")
      .then((r) => {
        setIsBusiness(r.data.customer_type === "business");
        setForm({ ...EMPTY, ...(r.data.business_data || {}) });
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (isBusiness && (!form.business_name.trim() || !form.vat_number.trim())) {
      toast.error("Ragione sociale e Partita IVA sono obbligatorie");
      return;
    }
    setSaving(true);
    try {
      await api.put("/profile/billing", {
        customer_type: isBusiness ? "business" : "private_consumer",
        business_data: isBusiness ? form : null,
      });
      toast.success("Dati di fatturazione salvati");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="billing-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" /> Dati di fatturazione
          </DialogTitle>
        </DialogHeader>
        {!loaded ? (
          <p className="text-sm text-gray-400 py-4">Caricamento…</p>
        ) : (
          <div className="space-y-3 py-2">
            <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-800">
              <div>
                <p className="text-sm font-semibold">Account business</p>
                <p className="text-xs text-gray-400">Richiedi fattura sui tuoi acquisti</p>
              </div>
              <Switch checked={isBusiness} onCheckedChange={setIsBusiness} data-testid="billing-business-toggle" />
            </div>
            {isBusiness && (
              <>
                <Input value={form.business_name} onChange={set("business_name")} placeholder="Ragione sociale *" data-testid="billing-business-name" />
                <Input value={form.vat_number} onChange={set("vat_number")} placeholder="Partita IVA *" data-testid="billing-vat-number" />
                <Input value={form.tax_code} onChange={set("tax_code")} placeholder="Codice fiscale" data-testid="billing-tax-code" />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={form.sdi_code} onChange={set("sdi_code")} placeholder="Codice SDI" data-testid="billing-sdi-code" />
                  <Input value={form.pec_email} onChange={set("pec_email")} placeholder="PEC" data-testid="billing-pec" />
                </div>
                <Input value={form.address} onChange={set("address")} placeholder="Indirizzo" data-testid="billing-address" />
                <div className="grid grid-cols-3 gap-2">
                  <Input value={form.city} onChange={set("city")} placeholder="Città" className="col-span-2" data-testid="billing-city" />
                  <Input value={form.postal_code} onChange={set("postal_code")} placeholder="CAP" data-testid="billing-postal-code" />
                </div>
                <Input value={form.country} onChange={set("country")} placeholder="Paese (IT)" maxLength={2} data-testid="billing-country" />
              </>
            )}
            <Button onClick={save} disabled={saving} className="w-full" data-testid="billing-save-btn">
              {saving ? "Salvataggio…" : "Salva"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
